<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '@/composable/useI18n'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { storeToRefs } from 'pinia'

const { t } = useI18n()
const store = useFareOptimizerStore()
const { peakWasteAnalysis, hasHighPeakToOffpeakRatio } = storeToRefs(store)

const hasAnalysis = computed(() => peakWasteAnalysis.value !== null)

const peakToOffpeakRatioLabel = computed(() => {
  const ratio = peakWasteAnalysis.value?.peak_to_offpeak_ratio ?? 0
  return `${ratio.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`
})

const potentialSavingsLabel = computed(() => {
  const savings = peakWasteAnalysis.value?.potential_savings ?? 0
  return useCurrencyFormaterFromCents(savings)
})
</script>

<template>
  <div v-if="hasAnalysis" class="bg-white shadow-4">
    <div class="flex items-center justify-between border-b-2 border-stroke px-6 py-4">
      <h2 class="font-satoshi text-base font-bold text-black">
        {{ t('contract_optimizer.peak_waste_analysis.title') }}
      </h2>
    </div>

    <div class="flex flex-col gap-4 p-6 sm:flex-row sm:items-stretch">
      <div
        class="flex flex-1 flex-col gap-2 border border-stroke p-4"
        :class="hasHighPeakToOffpeakRatio ? 'border-red bg-red/5' : 'bg-white'"
      >
        <span class="text-sm font-semibold text-black">
          {{ t('contract_optimizer.peak_waste_analysis.peak_to_offpeak_ratio') }}
        </span>
        <span
          class="text-xl font-semibold"
          :class="hasHighPeakToOffpeakRatio ? 'text-red' : 'text-black'"
        >
          {{ peakToOffpeakRatioLabel }}
        </span>
        <p
          v-if="hasHighPeakToOffpeakRatio"
          class="mt-1 text-sm font-medium text-red"
        >
          {{ t('contract_optimizer.peak_waste_analysis.high_ratio_alert') }}
        </p>
      </div>

      <div class="flex flex-1 flex-col gap-2 border border-stroke bg-white p-4">
        <span class="text-sm font-semibold text-black">
          {{ t('contract_optimizer.peak_waste_analysis.potential_savings') }}
        </span>
        <span class="text-xl font-semibold text-darkGreen">
          {{ potentialSavingsLabel }}
        </span>
      </div>
    </div>
  </div>
</template>
